import { useState } from "react";
import { motion } from "framer-motion";
import { Handshake, Copy, Check, ArrowUpRight } from "lucide-react";

const AGENCY_EMAIL = "hr@milestonetbhq.org";

export const RecruitmentPartners = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(AGENCY_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const range = document.createRange();
      const node = document.getElementById("agency-email-text");
      if (node) {
        range.selectNodeContents(node);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  return (
    <section
      data-testid="careers-recruitment-partners"
      className="py-20 md:py-28 border-t border-ink/10 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-12 gap-6 md:gap-10 items-start"
        >
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="w-4 h-4 text-steel-accent" strokeWidth={1.5} />
              <span className="overline text-forest-accent">
                / 04 — Recruitment Partners
              </span>
            </div>
            <h2
              className="font-display font-bold leading-[1.05] tracking-tight text-ink text-balance"
              style={{ fontSize: "clamp(1.75rem, 3.8vw, 3rem)" }}
            >
              For hiring agencies &
              <br />
              <span className="italic font-light text-amber-accent">search firms.</span>
            </h2>
            <p className="mt-6 text-ink-soft text-base md:text-[17px] leading-relaxed max-w-xl">
              Recruitment agencies, staffing partners and executive-search
              consultants — please introduce yourself to HR before sending
              profiles. We work with a small panel of vetted partners on
              specialist mandates and will respond to serious enquiries
              promptly.
            </p>
            <p className="mt-4 text-ink-soft text-sm leading-relaxed max-w-xl">
              Unsolicited profiles without a signed engagement letter cannot be
              acknowledged.
            </p>
          </div>

          {/* Email card */}
          <div
            data-testid="agency-email-card"
            className="col-span-12 md:col-span-6 border border-ink/10 bg-bone p-7 md:p-9"
          >
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-steel-accent mb-3">
              HR — Agency Desk
            </div>
            <span
              id="agency-email-text"
              data-testid="agency-email-text"
              className="font-display font-medium text-ink break-all leading-[1.1] cursor-text block"
              style={{
                fontSize: "clamp(1.25rem, 2.4vw, 2rem)",
                userSelect: "all",
                WebkitUserSelect: "all",
              }}
            >
              {AGENCY_EMAIL}
            </span>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={handleCopy}
                data-testid="agency-email-copy"
                className="group inline-flex items-center gap-1.5 bg-ink text-bone px-3.5 py-2 text-[13px] font-medium rounded-sm hover:bg-ink/85 transition-all"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-forest-accent" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
              <a
                href={`mailto:${AGENCY_EMAIL}?subject=Recruitment%20partnership%20enquiry`}
                data-testid="agency-email-mailto"
                className="group inline-flex items-center gap-1.5 border border-ink/20 text-ink px-3.5 py-2 text-[13px] font-medium rounded-sm hover:bg-ink/5 transition-colors"
              >
                Open in mail app
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>

            <div className="mt-6 pt-4 border-t border-ink/10 text-[12px] font-mono uppercase tracking-[0.18em] text-ink-soft">
              Please, no cold profiles — introduce yourself first.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
